import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useMutationQuestionImportList } from 'gql/mutations/question';
import extract from 'helpers/extract';
import { trackEvent, reportError } from 'lib/tracking';

import QuestionImportList from './QuestionImportList';

const QuestionImportListContainer = ({ course, handleModalClose }) => {
  const { t } = useTranslation();
  const mutationQuestionImportList = useMutationQuestionImportList();
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        chapterId: yup
          .string()
          .trim()
          .matches(
            /^|([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i,
          ),
        type: yup
          .string()
          .required(
            t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/type-error-required',
            ),
          )
          .trim()
          .oneOf(
            ['Flashcard', 'MCQ', 'ShortAnswer', 'Matching', 'Categorization'],
            t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/type-error-required',
            ),
          ),
        content: yup
          .string()
          .required(
            t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/content-error-required',
            ),
          )
          .trim(),
        rowSeparator: yup.string().required(),
        rowSeparatorOther: yup.string().when('rowSeparator', {
          is: 'other',
          then: yup
            .string()
            .required(
              t(
                'Course/Teacher/Editor/QuestionImport/QuestionImportList/row-separator-error-required',
              ),
            ),
          otherwise: yup.string(),
        }),
        columnSeparator: yup.string().required(),
        columnSeparatorOther: yup.string().when('columnSeparator', {
          is: 'other',
          then: yup
            .string()
            .required(
              t(
                'Course/Teacher/Editor/QuestionImport/QuestionImportList/column-separator-error-required',
              ),
            ),
          otherwise: yup.string(),
        }),
        switchOrder: yup.boolean().required(),
      }),
    [t],
  );
  const handleQuestionsImport = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const {
          rowSeparator,
          rowSeparatorOther,
          columnSeparator,
          columnSeparatorOther,
          ...variables
        } = extract(values, validationSchema);
        const {
          data: { questionImportList },
        } = await mutationQuestionImportList({
          ...variables,
          courseId: course.id,
          columnSeparator:
            columnSeparator === 'other'
              ? columnSeparatorOther
              : columnSeparator,
          rowSeparator:
            rowSeparator === 'other' ? rowSeparatorOther : rowSeparator,
        });
        handleModalClose(questionImportList);
        trackEvent('question', 'import-list');
      } catch (err) {
        form.setStatus('500');
        reportError('question', 'import-list', err);
      }
    },
    [course, handleModalClose, mutationQuestionImportList, validationSchema],
  );
  return (
    <QuestionImportList
      chapters={course.chapters}
      handleQuestionsImport={handleQuestionsImport}
      validationSchema={validationSchema}
      t={t}
    />
  );
};

QuestionImportListContainer.propTypes = {
  course: PropTypes.object.isRequired,
  handleModalClose: PropTypes.func.isRequired,
};

export default QuestionImportListContainer;
